import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Notification } from '@/components/Notification/Notification';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikDatePicker } from '@/components/form/fields/FormikDatePicker/FormikDatePicker';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { InvoiceLinkDetailsType } from '@/types/invoiceVaultTypes';
import { VaultAccountType } from '@/types/myVaultTypes';
import { loadVaultAccounts, MY_VAULT_QUERY_KEYS } from '@/bundle/_OrgAdmin/pages/MyVaultPage/api/api';
import { INVOICE_LINK_EXPIRY_OPTIONS, LINK_MODE_CARDS } from '../../const/const';
import { createInvoiceLink } from '../../api/api';
import { Steps } from '../shared/Steps/Steps';
import { InvoiceAccountCard, InvoiceAccountList, KeyValueList } from '../../styles';
import { ReadyToPasteCard } from './ReadyToPasteCard';
import { PageContent } from '@/components/Page/PageContent/PageContent';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { COLORS } from '@/styles/colors';
import { FormikSelectableTabs } from '@/components/form/fields/FormikSelectableTabs/FormikSelectableTabs';
import { InputError } from '@/components/form/fields/InputError/InputError';
import { FieldRow } from '@/components/form/FieldRow/FieldRow';
import { FormikRadioGroup } from '@/components/form/fields/FormikRadioGroup/FormikRadioGroup';
import { HighlightPanel } from '@/components-new/HighlightPanel/styles';
import { InvoiceLinks } from './ui/InvoiceLinks';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';

const STEP_LABELS = ['Choose account', 'Payment and link type', 'Generate'];

type FormValues = {
  vault_account_id: string;
  rail: string;
  mode: 'share' | 'verify' | '';
  expiry_code: '15d' | '30d' | '90d';
  invoice_number: string;
  amount: string;
  currency: string;
  due_date: Date | null;
  description: string;
};

const schema = Yup.object({
  vault_account_id: Yup.string().required('Choose the account the payer should pay into.'),
  rail: Yup.string().required('Choose the payment type.'),
  mode: Yup.string().required('Choose the link type.'),
  expiry_code: Yup.string().required(),
  invoice_number: Yup.string().trim().required('Invoice number is required.').max(64),
  amount: Yup.string()
    .trim()
    .matches(/^(\d+(\.\d{1,2})?)?$/, 'Enter an amount like 1850.00'),
  currency: Yup.string().trim().length(3),
  description: Yup.string().trim().max(255),
});

export const LinkAndQrTab = () => {
  const [accountSearch, setAccountSearch] = useState('');
  const [accountQuery, setAccountQuery] = useState('');
  const [step, setStep] = useState(1);
  const [apiError, setApiError] = useState('');
  const [generated, setGenerated] = useState<InvoiceLinkDetailsType | null>(null);

  const { data: accountsResponse, isPending: isAccountsPending } = useQuery({
    queryKey: [MY_VAULT_QUERY_KEYS.accounts, 'invoice_vault', accountQuery],
    queryFn: () => loadVaultAccounts({ limit: 100, q: accountQuery || undefined }),
    placeholderData: keepPreviousData,
  });
  const accounts: VaultAccountType[] = (accountsResponse?.body?.results ?? []).filter((a) => a.status !== 'closed');

  const submitAccountSearch = () => setAccountQuery(accountSearch.trim());

  const clearAccountSearch = () => {
    setAccountSearch('');
    setAccountQuery('');
  };

  const generate = useMutation({
    mutationFn: createInvoiceLink,
    onSuccess(response) {
      if (response.error) return setApiError(getResponseError(response.error));

      amplitudeService.logEvent(AMPLITUDE_EVENTS.InvoiceLinkGenerated);
      setApiError('');
      setGenerated(response.body);
    },
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      vault_account_id: '',
      rail: '',
      mode: 'verify',
      expiry_code: '30d',
      invoice_number: '',
      amount: '',
      currency: 'USD',
      due_date: null,
      description: '',
    },
    validationSchema: schema,
    onSubmit: (values) =>
      generate.mutate({
        vault_account_id: values.vault_account_id,
        rail: values.rail,
        mode: values.mode as 'share' | 'verify',
        expiry_code: values.expiry_code,
        invoice_number: values.invoice_number.trim(),
        amount: values.amount || undefined,
        currency: values.currency,
        due_date: values.due_date ? values.due_date.toISOString().slice(0, 10) : null,
        description: values.description,
      }),
  });

  const account = useMemo(
    () => accounts.find((a) => a.id === formik.values.vault_account_id),
    [accounts, formik.values.vault_account_id]
  );
  const railCards = (account?.rail_labels ?? []).map((r) => ({
    value: r.code,
    label: r.label,
    description: `Payer gets ${r.label} instructions for ${account?.nickname}.`,
  }));
  const railLabel = account?.rail_labels.find((r) => r.code === formik.values.rail)?.label ?? '';
  const modeLabel = LINK_MODE_CARDS.find((m) => m.value === formik.values.mode)?.label ?? '';

  const next = async () => {
    const errors = await formik.validateForm();
    const keys: (keyof FormValues)[] = step === 1 ? ['vault_account_id'] : ['rail', 'mode'];
    const blocking = keys.filter((k) => errors[k]);

    if (blocking.length) return formik.setTouched(Object.fromEntries(blocking.map((k) => [k, true])), true);

    setStep((stepIndex) => stepIndex + 1);
  };

  const reset = () => {
    setGenerated(null);
    setStep(1);
    formik.resetForm();
  };

  return (
    <Box display='grid' gap='24px' marginTop='24px'>
      <PageContent>
        <PageColumn>
          <DetailsWidget header='Put a verified payment link on your invoice' headerOffset={0}>
            <Typography variant='bodySmall' marginBottom='16px'>
              The payer opens the link or scans the QR, confirms the invoice number, and sees your bank instructions
              straight from your vault. Nothing is typed from the PDF.
            </Typography>
            <Steps labels={STEP_LABELS} current={step} />

            {step === 1 && (
              <Box marginBottom='12px'>
                <SearchInput
                  name='invoice-account-search'
                  width='420px'
                  placeholder='Search by account name or bank'
                  value={accountSearch}
                  hasErrorComponent={false}
                  onChange={(value) => setAccountSearch(value)}
                  onClear={clearAccountSearch}
                  onSubmit={submitAccountSearch}
                />
              </Box>
            )}

            <FormikForm value={formik}>
              {step === 1 && (
                <Box display='flex' flexDirection='column' gap='12px'>
                  <Typography variant='bodySmall' fontWeight='medium'>
                    Which account should the payer pay into?
                  </Typography>
                  {accounts.length === 0 && !isAccountsPending && (
                    <Notification variant='info'>
                      {accountQuery ? `No account matches "${accountQuery}".` : 'Add an account in My Vault first.'}
                    </Notification>
                  )}

                  {!!accounts.length && (
                    <InvoiceAccountList>
                      {accounts.map((a) => (
                        <InvoiceAccountCard
                          key={a.id}
                          type='button'
                          $active={formik.values.vault_account_id === a.id}
                          onClick={() => formik.setValues({ ...formik.values, vault_account_id: a.id, rail: '' })}
                        >
                          <Typography fontWeight='medium'>{a.nickname}</Typography>
                          <Typography variant='bodySmall'>
                            {a.bank_name} {a.masked_number} ·{' '}
                            <Box display='inline-flex' gap='6px'>
                              {a.rail_labels.map((rail) => (
                                <Box
                                  as='span'
                                  display='inline-block'
                                  key={rail.code}
                                  background={COLORS.mint[200]}
                                  padding='4px 6px'
                                  borderRadius='4px'
                                >
                                  <Typography variant='caption' color={COLORS.green[600]} fontWeight='semibold'>
                                    {rail.label}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Typography>
                        </InvoiceAccountCard>
                      ))}
                    </InvoiceAccountList>
                  )}

                  {formik.touched.vault_account_id && formik.errors.vault_account_id && (
                    <InputError error={formik.errors.vault_account_id}></InputError>
                  )}
                </Box>
              )}

              {step === 2 && (
                <Box display='flex' flexDirection='column'>
                  <FormikSelectableTabs
                    options={railCards}
                    name='rail'
                    direction='row'
                    label={<b>Payment type for {account?.nickname}</b>}
                    hasErrorComponent
                  />

                  <FormikSelectableTabs
                    name='mode'
                    options={LINK_MODE_CARDS}
                    direction='row'
                    label={<b>Link type</b>}
                    hasErrorComponent
                  />
                </Box>
              )}

              {step === 3 && (
                <Box display='flex' flexDirection='column' gap='12px'>
                  <HighlightPanel>
                    <KeyValueList>
                      <dt>Account</dt>
                      <dd>
                        {account?.nickname} · {account?.bank_name} {account?.masked_number}
                      </dd>
                      <dt>Payment type</dt>
                      <dd>{railLabel}</dd>
                      <dt>Link type</dt>
                      <dd>{modeLabel}</dd>
                    </KeyValueList>
                  </HighlightPanel>
                  <Box>
                    <FieldRow columns={[6, 6]}>
                      <FormikInput name='invoice_number' label='Invoice number' placeholder='INV-1050' />
                      <FormikRadioGroup name='expiry_code' label='Link expires' options={INVOICE_LINK_EXPIRY_OPTIONS} />
                    </FieldRow>

                    <FieldRow columns={[6, 6]}>
                      <FormikInput name='amount' label='Amount (optional)' placeholder='1850.00' />
                      <FormikInput name='currency' label='Currency' placeholder='USD' />
                    </FieldRow>

                    <FieldRow columns={[6, 6]}>
                      <FormikDatePicker
                        name='due_date'
                        label='Due date (optional)'
                        placeholder='Pick a date'
                        isClearable
                      />
                      <FormikInput
                        name='description'
                        label='Memo on the payer page (optional)'
                        placeholder='Design retainer, September'
                      />
                    </FieldRow>
                  </Box>
                  <Typography variant='bodySmall'>
                    The invoice number is the payer's first check: they must type it before the instructions show. Keep
                    it exactly as printed on the invoice.
                  </Typography>
                </Box>
              )}

              {apiError && (
                <Notification marginTop='16px' variant='error'>
                  {apiError}
                </Notification>
              )}

              <DialogButtons>
                <ButtonNew
                  variant='secondary'
                  size='medium'
                  disabled={step === 1}
                  onClick={() => setStep((stepIndex) => stepIndex - 1)}
                >
                  Back
                </ButtonNew>
                {step < 3 ? (
                  <ButtonNew size='medium' onClick={next} disabled={accounts.length === 0}>
                    Continue
                  </ButtonNew>
                ) : (
                  <ButtonNew size='medium' type='submit' isLoading={generate.isPending}>
                    Generate link and QR code
                  </ButtonNew>
                )}
              </DialogButtons>
            </FormikForm>
          </DetailsWidget>
        </PageColumn>
        <PageColumn>
          <ReadyToPasteCard link={generated} railLabel={railLabel} onNew={reset} />
        </PageColumn>
      </PageContent>

      <InvoiceLinks />
    </Box>
  );
};
